import React from "react";
import { fmtEur, fmtPct } from "../labels";

export const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
export const fmtMonth = (ym) => (ym && ym.length >= 7 ? `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}` : ym || "—");
export const fmtK = (v) => (v == null ? "—" : v >= 100000 ? `${Math.round(v / 1000)} k€` : fmtEur(v));

export function Reliab({ level, className = "" }) {
  if (!level) return null;
  const ok = level === "fiable";
  return (
    <span data-testid="kk-reliab" className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${ok ? "border-emerald-700/40 text-emerald-800 bg-emerald-50" : "border-clay-300 text-clay-500 bg-clay-50"} ${className}`}>
      {ok ? "fiable" : "indicio"}
    </span>
  );
}

export function Delta({ pct, testid }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return <span data-testid={testid} className={`text-xs tabular ${up ? "text-emerald-800" : "text-terracotta"}`}>{up ? "+" : ""}{String(pct).replace(".", ",")} %</span>;
}

export function Big({ label, value, sub, delta, testid }) {
  return (
    <div data-testid={testid} className="min-w-[120px]">
      <div className="smallcaps">{label}</div>
      <div className="font-serif text-3xl md:text-4xl leading-none mt-1 tabular">{value}</div>
      <div className="text-[11px] text-clay-600 mt-1 flex items-center gap-2">{sub}{delta}</div>
    </div>
  );
}

/* Horizontal bars. rows: [{ key, label, value, text, sub, onClick, muted, tone }] */
export function HBars({ rows, max, tone = "bg-terracotta", labelW = "w-28 md:w-36", testid, compact = false }) {
  const m = max ?? Math.max(...rows.map((r) => r.value || 0), 0.0001);
  return (
    <div className={compact ? "space-y-1" : "space-y-1.5"} data-testid={testid}>
      {rows.map((r) => (
        <div key={r.key ?? r.label} className="flex items-center gap-2 text-[12px]" data-testid={`${testid}-${r.key ?? r.label}`}>
          <div className={`${labelW} shrink-0 truncate ${r.muted ? "text-clay-400" : "text-clay-800"}`} title={r.label}>{r.label}</div>
          <button type="button" onClick={r.onClick} disabled={!r.onClick}
            className={`flex-1 h-4 bg-clay-100/70 relative text-left ${r.onClick ? "cursor-pointer hover:bg-clay-200/70" : "cursor-default"}`} aria-label={`${r.label}: ${r.text}`}>
            <div className={`h-full ${r.muted ? "bg-clay-300" : r.tone || tone} transition-[width] duration-500`} style={{ width: `${Math.min(100, ((r.value || 0) / m) * 100)}%` }}/>
          </button>
          <div className={`shrink-0 tabular text-right ${compact ? "w-16" : "w-28 md:w-40"} ${r.muted ? "text-clay-400" : ""}`}>
            <span className="font-medium">{r.text}</span>{!compact && r.sub && <span className="text-clay-500 text-[11px]"> {r.sub}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Two bars per row, each series scaled to its own max. series: [{ key, label, tone, format }] */
export function PairBars({ rows, series, testid, onClick, labelW = "w-20" }) {
  const max = Object.fromEntries(series.map((s) => [s.key, Math.max(...rows.map((r) => r[s.key] || 0), 0.0001)]));
  return (
    <div data-testid={testid}>
      <div className="flex gap-4 text-[10px] uppercase tracking-wider text-clay-500 mb-2" style={{ paddingLeft: "5.5rem" }}>
        {series.map((s) => <span key={s.key} className="inline-flex items-center gap-1"><i className={`inline-block w-2.5 h-2.5 ${s.tone}`}/>{s.label}</span>)}
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <button type="button" key={r.key} onClick={onClick ? () => onClick(r) : undefined} disabled={!onClick}
            className={`w-full flex items-center gap-2 text-[12px] text-left ${onClick ? "hover:bg-clay-100/60" : "cursor-default"}`} data-testid={`${testid}-${r.key}`}>
            <div className={`${labelW} shrink-0 text-clay-800`}>{r.label}</div>
            <div className="flex-1 space-y-0.5">
              {series.map((s) => (
                <div key={s.key} className="flex items-center gap-2">
                  <div className="flex-1 h-2.5 bg-clay-100/70"><div className={`h-full ${s.tone} transition-[width] duration-500`} style={{ width: `${((r[s.key] || 0) / max[s.key]) * 100}%` }}/></div>
                  <div className="w-20 md:w-24 shrink-0 tabular text-right text-[11px]">{s.format(r[s.key])}</div>
                </div>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export const convOf = (r) => (r.conversion == null ? "—" : fmtPct(r.conversion));
